/**
 * Notification Service – CRUD + Realtime subscription
 * Sprint 6 – standardized DAL
 */
import { supabase, handleError, type ServiceResult, type PaginatedResult, getPaginationRange } from './base.service';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface NotificationRow {
  id: string;
  user_id: string;
  title: string;
  message: string;
  link?: string;
  status_code: string;
  created_at: string;
  read_at?: string;
}

export interface NotificationFilters {
  status_code?: 'unread' | 'read' | 'all';
  page?: number;
  pageSize?: number;
}

const notificationService = {
  /** List notifications for current user */
  async list(userId: string, filters: NotificationFilters = {}): Promise<PaginatedResult<NotificationRow>> {
    try {
      const { from, to } = getPaginationRange(filters.page, filters.pageSize ?? 20);
      let query = supabase
        .from('notifications')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filters.status_code && filters.status_code !== 'all') {
        query = query.eq('status_code', filters.status_code);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: (data ?? []) as NotificationRow[], count: count ?? 0, error: null };
    } catch (err) {
      return { data: [], count: 0, error: handleError(err) };
    }
  },

  /** Count unread */
  async countUnread(userId: string): Promise<ServiceResult<number>> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status_code', 'unread');
      if (error) throw error;
      return { data: count ?? 0, error: null };
    } catch (err) {
      return { data: 0, error: handleError(err) };
    }
  },
  
  /** Create a new notification */
  async create(payload: { user_id: string; title: string; message: string; link?: string }): Promise<ServiceResult<NotificationRow>> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          ...payload,
          status_code: 'unread',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      if (error) throw error;
      return { data: data as NotificationRow, error: null };
    } catch (err) {
      return { data: null as any, error: handleError(err) };
    }
  },

  /** Mark single notification as read */
  async markRead(id: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ status_code: 'read', read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Mark all notifications as read for user */
  async markAllRead(userId: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ status_code: 'read', read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('status_code', 'unread');
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Subscribe to realtime notifications for user */
  subscribeToNotifications(
    userId: string,
    onNew: (notification: NotificationRow) => void
  ): RealtimeChannel {
    return supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          onNew(payload.new as NotificationRow);
        }
      )
      .subscribe();
  },

  /** Unsubscribe channel */
  unsubscribe(channel: RealtimeChannel): void {
    supabase.removeChannel(channel);
  },
};

export default notificationService;
